import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getSYS3010_R01List } from "src/containers/mes/SYS/SYS3010_R01/Api_SYS3010_R01";
import { defaultSYS3020_R01Filter, SYS3020_R01Filter, useSYS3020_R01Store } from "src/containers/mes/SYS/SYS3020_R01/Store_SYS3020_R01";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent";
import {
	deleteMenuAuthority,
	downloadExcel_SYS3020_R01,
	getMenuAuthorityAllCount,
	getMenuAuthorityList,
	upsertMenuAuthority
} from "./Api_SYS3020_R01";
import { defaultSYS3020_R01Dto, SYS3020_R01Dto } from "./Dto_SYS3020_R01";
import { showSuccess, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { buildCommonCodeRequirements, SYS3020_R01_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";


interface Props {
	gridRef: React.RefObject<AgGridReact<SysCodeDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SYS3020_R01Dto> | null>;
	setMessage: (message: string) => void;
	
}

/**
 * 권한 그룹별 메뉴 권한 관리 Content 컴포넌트
 * 액션바(조회/저장/삭제/초기화/엑셀)와 주요 로직 담당
 */
export const Content_SYS3020_R01 = (props: Props) => {

	const { gridRef, detailGridRef } = props;
	const { setMessage } = useUiuxStore();
	const { yesNoList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const {
		SYS3020_R01Filter,
		SYS3020_R01PastFilter, setSYS3020_R01PastFilter,
		SYS3020_R01List, setSYS3020_R01List,
		selectedSYS3020_R01, setSelectedSYS3020_R01,
		SYS3020_R01DetailList: menuAuthorityList, setSYS3020_R01DetailList: setMenuAuthorityList, insertSYS3020_R01DetailList: insertMenuAuthorityList,
		deletedSYS3020_R01DetailList: deletedMenuAuthorityList, addDeletedSYS3020_R01DetailList: addDeletedMenuAuthorityList, removeDeletedSYS3020_R01DetailList: removeDeletedMenuAuthorityList,
		selectedSYS3020_R01List: selectedMenuAuthorityList, setSelectedSYS3020_R01List: setSelectedMenuAuthorityList, removeSelectedSYS3020_R01List: removeSelectedMenuAuthorityList,
		originalSYS3020_R01DetailList, setOriginalSYS3020_R01DetailList,
		allCount, setAllCount,
		initData,
		setScrollTop,
		setDetailScrollTop,
		detailFetchTrigger, setDetailFetchTrigger,
	} = useSYS3020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { success, error: showError } = useToast();
	
	// 권한 정보 로드 (useFetchActionbarAuth 훅 사용)
	useFetchActionbarAuth("SYS3020_R01");

	const defaultSYS3020_R01DetailRow = useCallback((): SYS3020_R01Dto => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS3020_R01_ROW_VALIDATE, {
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultSYS3020_R01Dto }; }
		return {
			...defaultSYS3020_R01Dto,
			userId: selectedSYS3020_R01 || '',
			queryAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			insertAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			deleteAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			saveAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			printAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			excelAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			useYn: 'Y',
			inserted: true,
		}
	}, [selectedSYS3020_R01]);

	const prevMenuCdRef = useRef(SYS3020_R01Filter.menuCd);

	// 엑셀 다운로드 핸들러
	const handleExcelClick = useCallback(async () => {
		if (!selectedSYS3020_R01) {
			showError("사용자를 선택해주세요.");
			return;
		}
		setLoadingSpinner(true);

		const params = {
			userId: selectedSYS3020_R01 || '',
			systemCd: SYS3020_R01Filter.systemCd || '',
			menuCd: SYS3020_R01Filter.menuCd || '',
			useYn: SYS3020_R01Filter.useYn || ''
		};

		const response = await downloadExcel_SYS3020_R01(
			'',
			params,
			SYS3020_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '사용자 권한 등록-excel.xlsx' });		
		setLoadingSpinner(false);
	}, [SYS3020_R01List, selectedSYS3020_R01, SYS3020_R01Filter, success, showError]);


	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMenuAuthorityAllCount(getParams(SYS3020_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS3020_R01Filter, setAllCount]);

	// 통합된 조회 함수
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);
		try {

				// 1. 좌측 테이블 - 사용자 목록 조회 (권한그룹 목록 대신)
				const userResponse = await getSYS3010_R01List(
					'',
					{ userNm: SYS3020_R01Filter.userId }, // 사용자 ID로 검색
					SYS3020_R01List.length ?? 0
				);
				let userList: SysCodeDto[] = [];
				if (userResponse.data.successFlag) {
					userList = userResponse.data.data.map(user => ({
						valueIndex: 0,
						orderIndex: 0,
						orderIndexSeq: 0,
						majorCd: '',
						minorCd: user.userId,
						codeNm: user.userNm,
						sysFlg: 'Y',
						dispSeq: 0,
						ref01: '',
						ref02: '',
						ref03: '',
						ref04: '',
						ref05: '',
						ref06: '',
						ref07: '',
						ref08: '',
						ref09: '',
						ref10: '',
						remark: user.empNm || ''
					})) as SysCodeDto[];


					// userId(또는 userNm)로 필터링
					if (SYS3020_R01Filter.userId) {
						userList = userList.filter(
								user =>
									user.minorCd.includes(SYS3020_R01Filter.userId) ||
									user.codeNm.includes(SYS3020_R01Filter.userId)
							)
						}
				}
				
				let newUserList = [...userList] as SysCodeDto[];

				setSYS3020_R01List(newUserList);
				// 첫 번째 사용자 자동 선택 (사용자가 선택되지 않은 경우에만)
				if (newUserList.length > 0 && !selectedSYS3020_R01) {
					const firstUserId = newUserList[0].minorCd;
					setSelectedSYS3020_R01(firstUserId);
				}
			
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다.`);
			setTimeout(() => setMessage(""), 5000);
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [SYS3020_R01Filter, SYS3020_R01List, setSYS3020_R01List, selectedSYS3020_R01, setMessage, showError, setScrollTop, setDetailScrollTop]);


	const getParams = (filter: SYS3020_R01Filter, selectedUserId?: string) => {
    return {
        userId: selectedUserId || filter.userId,  // 선택된 사용자 우선
        systemCd: filter.systemCd || '',
        menuCd: filter.menuCd || '',
        useYn: filter.useYn || ''
    };
};

	// 메뉴 권한 목록 조회 함수 (사용자 선택 시 호출)
	const fetchMenuAuthorityData = useCallback(async (userId: string) => {
		setLoadingSpinner(true);
		
		try {

			const { isDefaultFilter, isDefaultPastFilter, listLength } = fetchFilterConfig(
				SYS3020_R01Filter,
				SYS3020_R01PastFilter,
				defaultSYS3020_R01Filter,
				SYS3020_R01List.length
			);
			
			// userId를 직접 사용하도록 수정
			
			const response = await getMenuAuthorityList(
				isDefaultFilter? '' : 'search',
				getParams(SYS3020_R01Filter, userId),
				listLength
			);
			
			if (response.data.successFlag) {
				const menuAuthorityData = Array.isArray(response.data.data) ? response.data.data : [];
				const processAllCount = await renewalAllCount();

				if (menuAuthorityData.length === 0) {
					setMenuAuthorityList([]);
					insertMenuAuthorityList([{ ...defaultSYS3020_R01DetailRow() }]);
				} else {
					setMenuAuthorityList(menuAuthorityData);
					setOriginalSYS3020_R01DetailList(menuAuthorityData);
				}

				detailGridRef.current?.api.ensureIndexVisible(0, 'top');
				ShowSuccessFetch(setMessage, `메뉴권한 조회가 완료되었습니다. (총 ${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				showError(response.data.message || "메뉴 권한 목록 조회에 실패했습니다.");
			}
			
		} catch (error) {
			console.error("메뉴 권한 데이터 조회 중 오류 발생:", error);
			showError("메뉴 권한 데이터 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [SYS3020_R01Filter, SYS3020_R01PastFilter, SYS3020_R01List, setMenuAuthorityList, setOriginalSYS3020_R01DetailList, insertMenuAuthorityList, defaultSYS3020_R01DetailRow, showError, renewalAllCount, setMessage, detailGridRef]);

	// 선택됐는데 리스트가 비어있으면 신규행 삽입 (안전망)
	useEffect(() => {
		if (selectedSYS3020_R01 && menuAuthorityList.length === 0) {
			insertMenuAuthorityList([{ ...defaultSYS3020_R01DetailRow() }]);
		}
	}, [selectedSYS3020_R01, menuAuthorityList.length, insertMenuAuthorityList, defaultSYS3020_R01DetailRow]);

	// 사용자 선택 핸들러 (사용자 선택 시 자동으로 메뉴 권한 조회)
	const handleTableRowClick = useCallback(async (rowData: any) => {
		const newUserId = rowData?.minorCd || rowData?.userId;

		
		if (selectedSYS3020_R01 === newUserId) {
			// 같은 행 클릭 시 선택 해제
			setSelectedSYS3020_R01(undefined);
			setMenuAuthorityList([]);
		} else {
			// 다른 행 클릭 시 선택하고 메뉴 권한 조회
			setSelectedSYS3020_R01(newUserId);
			if (newUserId) {
				await fetchMenuAuthorityData(newUserId);
			} else {
				setMenuAuthorityList([]);
			}
		}
	}, [selectedSYS3020_R01, setSelectedSYS3020_R01, setMenuAuthorityList, fetchMenuAuthorityData]);

	// 초기화 핸들러
	const initContainerData = useCallback(() => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	// 저장 핸들러
	const handleSaveClick = useCallback(async () => {
		if (!selectedSYS3020_R01) {
			showError("사용자를 선택해주세요.");
			return;
		}

		const invalidRows = menuAuthorityList.filter(
			item => item.inserted && (!item.menuCd || item.menuCd.trim() === '')
		);
		if (invalidRows.length > 0) {
			showError("메뉴코드가 없는 행이 있습니다. 메뉴를 선택해주세요.");
			return;
		}

		if (menuAuthorityList.length === 0) {
			showError("저장할 메뉴 권한 데이터가 없습니다.");
			return;
		}

		setLoadingSpinner(true);
		try {
			const deletedList = deletedMenuAuthorityList.map(item => 
				menuAuthorityList.find(menu => menu.valueIndex === Number(item))
			);
			const modifiedList = menuAuthorityList.filter(item =>
				(item.inserted || item.isModified) &&
				!deletedMenuAuthorityList.includes(item.valueIndex.toString())
			);

			// 삭제 처리
			for (const item of deletedList) {
				if (item?.menuCd && !item.inserted) {
					await deleteMenuAuthority(selectedSYS3020_R01, item.menuCd);
				}
			}
			
			// 등록/수정 처리
			for (const item of modifiedList) {
				const saveData = {
					...item,
					userId: selectedSYS3020_R01,
				};
				await upsertMenuAuthority(saveData);
			}

			const successMessages = [];
			if (deletedList.filter(Boolean).length > 0) successMessages.push(`${deletedList.filter(Boolean).length}건 삭제`);
			if (modifiedList.length > 0) successMessages.push(`${modifiedList.length}건 저장`);
			
			if (successMessages.length > 0) {
				success(`${successMessages.join(', ')} 완료되었습니다.`);
			} else {
				showError("저장할 데이터가 없습니다.");
			}
			
			removeDeletedMenuAuthorityList(deletedMenuAuthorityList);
			await fetchMenuAuthorityData(selectedSYS3020_R01);
			
		} catch (error: any) {
			console.error("저장 중 오류 발생:", error);
			showError(`저장 중 오류가 발생했습니다: ${error.message || error}`);
		} finally {
			setLoadingSpinner(false);
		}
	}, [menuAuthorityList, deletedMenuAuthorityList, selectedSYS3020_R01, fetchMenuAuthorityData, success, showError, removeDeletedMenuAuthorityList]);

	// 삭제 핸들러
	const handleDeleteClick = useCallback(() => {
		if (deletedMenuAuthorityList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedMenuAuthorityList(deletedMenuAuthorityList);
			removeSelectedMenuAuthorityList();
		} else if (selectedMenuAuthorityList.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedMenuAuthorityList(selectedMenuAuthorityList);
			removeSelectedMenuAuthorityList(selectedMenuAuthorityList);
		} else {
			console.log('선택된 행이 없음');
		}
	}, [selectedMenuAuthorityList, deletedMenuAuthorityList, addDeletedMenuAuthorityList, removeDeletedMenuAuthorityList, removeSelectedMenuAuthorityList]);

	// 검색 조건 변경 시 자동 조회
	useEffect(() => {
		if (!detailFetchTrigger) return;
		if (selectedSYS3020_R01) {
			if (prevMenuCdRef.current !== SYS3020_R01Filter.menuCd) {
				setDetailScrollTop(null);
				prevMenuCdRef.current = SYS3020_R01Filter.menuCd;
			}
			fetchMenuAuthorityData(selectedSYS3020_R01);
			setSelectedMenuAuthorityList([]);
		}
		setDetailFetchTrigger(false);
	}, [SYS3020_R01Filter.menuCd, selectedSYS3020_R01, setLoadingSpinner, setMenuAuthorityList, setDetailScrollTop]);

	return {
		handleExcelClick,
		defaultSYS3020_R01DetailRow,
		fetchData,
		fetchMenuAuthorityData,
		initContainerData,
		handleSaveClick,
		handleDeleteClick,
		handleTableRowClick,
	};
};